import { ReactNode, useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { getApiKey } from "../lib/apiKey";
import { theme } from "../theme";
import { APP_META } from "../appMeta";
import SiteBackground from "./SiteBackground";
import SupportDock from "./SupportDock";

type NavItemDef = { to: string; label: string; end?: boolean; note?: string };

function NavItem({
  to,
  label,
  end,
  compact = false,
}: {
  to: string;
  label: string;
  end?: boolean;
  compact?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        [
          compact ? "px-2.5 py-1.5 rounded-lg text-xs" : "px-3 py-2 rounded-xl text-sm",
          "transition border",
          "nav-matrix",
          isActive
            ? "glass text-white border-white/15"
            : "text-white/70 hover:text-white hover:bg-white/5 border-white/10 bg-white/5",
        ].join(" ")
      }
    >
      {label}
    </NavLink>
  );
}

export default function AppShell({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hasSavedSession, setHasSavedSession] = useState<boolean>(() => Boolean(getApiKey()));

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const sync = () => setHasSavedSession(Boolean(getApiKey()));
    window.addEventListener("storage", sync);
    window.addEventListener("bp-auth-changed", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("bp-auth-changed", sync);
    };
  }, []);

  // ✅ Update these to match your actual routes
  const primaryNav: NavItemDef[] = useMemo(
    () => [
      { to: "/explore", label: "Explore" },
      { to: "/reports", label: "Reports" },
      { to: "/aquaculture", label: "Aquaculture", note: "farm" },
    ],
    []
  );

  const utilityNav: NavItemDef[] = useMemo(
      () => [
        { to: "/", label: "Home", end: true },
        { to: "/about", label: "About" },
        { to: "/docs", label: "Docs", note: "API" },
        { to: "/faq", label: "FAQ" },
        { to: "/pricing", label: "Pricing", note: "plans" },
        { to: "/dev", label: "Dev", note: "admin" },
        { to: "/settings", label: "Settings" },
        { to: "/contact", label: "Contact" },
    ],
    []
  );

  const pathLabel = useMemo(() => {
    if (location.pathname === "/") return "Home";
    return location.pathname;
  }, [location.pathname]);

  const headerUtilityNav = useMemo(
    () => utilityNav.filter((item) => item.to !== "/dev" && item.to !== "/settings"),
    [utilityNav]
  );
  const operatorUtilityNav = useMemo(
    () => utilityNav.filter((item) => item.to === "/dev" || item.to === "/settings"),
    [utilityNav]
  );

  return (
    <div className="min-h-screen overflow-x-hidden">
      {/* ✅ Keep the living background/effects on ALL pages (including Explore) */}
      <SiteBackground />

      <header className="sticky top-0 z-40">
        <div className="glass border-b border-white/10">
          <div className="mx-auto max-w-7xl px-3 sm:px-6">
            <div className="flex min-h-[3.25rem] items-center justify-between gap-2 py-2 sm:min-h-[3.5rem] sm:gap-3">
              <Link to="/" className="flex items-center gap-2 min-w-0">
                <div className="h-8 w-8 rounded-2xl glass-soft grid place-items-center border border-white/10 shrink-0">
                  <span className="text-sm font-semibold">BP</span>
                </div>
                <div className="leading-tight min-w-0">
                  <div className="text-sm font-semibold tracking-wide truncate">{theme.brand.name}</div>
                  <div className="text-[11px] text-white/60 hidden sm:block truncate">
                    {theme.brand.tagline}
                  </div>
                </div>
              </Link>

              {/* Desktop nav */}
              <nav className="hidden md:flex items-center gap-2 shrink-0">
                {primaryNav.map((it) => (
                  <NavItem key={it.to} to={it.to} label={it.label} end={it.end} />
                ))}
              </nav>

              {/* Right side: launch CTA + account */}
              <div className="hidden md:flex items-center gap-2">
                {!hasSavedSession && (
                  <Link
                    to="/launch"
                    className="shrink-0 rounded-xl border border-teal-400/40 bg-teal-900/30 px-3 py-1.5 text-xs font-semibold text-teal-200 transition hover:bg-teal-800/40 hover:text-teal-100"
                  >
                    Get early access →
                  </Link>
                )}
                <NavItem to="/account" label={hasSavedSession ? "Account" : "Sign in"} />
              </div>

              {/* Mobile menu button */}
              <button
                type="button"
                className="md:hidden nav-matrix rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/80 transition"
                onClick={() => setMobileOpen((v) => !v)}
                aria-expanded={mobileOpen}
                aria-label="Open menu"
              >
                Menu ▾
              </button>
            </div>

            <div className="hidden md:flex items-center justify-between gap-3 border-t border-white/5 py-2">
              <div className="flex flex-wrap items-center gap-2">
                {headerUtilityNav.map((it) => (
                  <NavItem key={it.to} to={it.to} label={it.label} end={it.end} compact />
                ))}
              </div>
              {operatorUtilityNav.length ? (
                <div className="flex items-center gap-2">
                  {operatorUtilityNav.map((it) => (
                    <NavItem key={it.to} to={it.to} label={it.label} end={it.end} compact />
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* Mobile menu panel */}
        {mobileOpen ? (
          <div className="md:hidden border-b border-white/10 bg-[rgba(8,14,24,0.84)] shadow-[0_16px_40px_rgba(0,0,0,0.36)] backdrop-blur-xl">
            <div className="mx-auto grid max-w-7xl gap-2 px-3 py-3 sm:px-6">
              <div className="text-[11px] uppercase tracking-[0.22em] text-white/55">Navigate</div>
              <div className="grid grid-cols-1 gap-2 min-[420px]:grid-cols-2">
                {primaryNav.map((it) => (
                  <NavItem key={it.to} to={it.to} label={it.label} end={it.end} />
                ))}
                {utilityNav.map((it) => (
                  <NavItem key={it.to} to={it.to} label={it.label} end={it.end} />
                ))}
                <NavItem to="/account" label={hasSavedSession ? "Account" : "Sign in"} />
              </div>
            </div>
          </div>
        ) : null}

        <div className="mx-auto mt-1.5 max-w-7xl px-3 sm:mt-3 sm:px-6">
          <div className="truncate text-[11px] text-white/50 sm:text-xs">{pathLabel}</div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-3 pb-[calc(5rem+env(safe-area-inset-bottom))] pt-3 sm:px-6 sm:pb-16 sm:pt-4">
        {children}
      </main>

      <footer className="border-t border-white/10 bg-[rgba(4,10,18,0.78)]">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-3 py-3 text-[11px] text-white/55 sm:px-6 sm:text-xs">
          <div className="flex flex-wrap gap-x-4 gap-y-2 text-white/60">
            {utilityNav
              .filter((item) => item.to !== "/settings" && item.to !== "/dev")
              .map((item) => (
                <Link key={item.to} to={item.to} className="hover:text-white transition">
                  {item.label}
                </Link>
              ))}
          </div>
          <div>{APP_META.name} v{APP_META.version}</div>
          <div>Updated {APP_META.lastUpdated}</div>
        </div>
      </footer>
      <SupportDock />
    </div>
  );
}
